import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { CourierHttpError, createCourier } from '@vielzeug/courier';
import { s } from '@vielzeug/spell';

import type { AuditLogApi } from './api';
import type { AuditLogEntry, AuditLogSearchParams, PagedResult } from './types';

const eventSchema = s.record(s.string(), s.unknown());
const auditLogEntrySchema = s.object({
  id: s.string(),
  createdAt: s.string(),
  expiresAt: s.string().nullable(),
  eventType: s.string(),
  eventAt: s.string(),
  eventBy: s.string(),
  event: eventSchema,
});
const pageMetadataSchema = s.object({
  size: s.number(),
  number: s.number(),
  totalElements: s.number(),
  totalPages: s.number(),
});
const auditLogPageSchema = s.object({
  content: s.array(auditLogEntrySchema),
  page: pageMetadataSchema,
});
/** HTTP client for the TDMS Audit Log API through the Backstage proxy. */
export class AuditLogApiClient implements AuditLogApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private courierPromise?: Promise<ReturnType<typeof createCourier>>;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async searchLogs({ filters, page, signal }: AuditLogSearchParams) {
    const courier = await this.getCourier();
    return this.withProblemDetail(
      courier.post<PagedResult<AuditLogEntry>>('/audit-logs/search', {
        body: filters ?? {},
        query: {
          page: page?.page ?? 0,
          size: page?.size ?? 25,
          sort: page?.sort ?? ['eventAt,desc'],
        },
        signal,
        schema: auditLogPageSchema,
      }),
    );
  }

  async getLogById(id: string, signal?: AbortSignal) {
    const courier = await this.getCourier();

    return this.withProblemDetail(
      courier.get<AuditLogEntry>(`/audit-logs/${encodeURIComponent(id)}`, {
        signal,
        schema: auditLogEntrySchema,
      }),
    );
  }

  private getCourier(): Promise<ReturnType<typeof createCourier>> {
    if (!this.courierPromise) {
      const courierPromise = this.discoveryApi
        .getBaseUrl('audit-log')
        .then(baseUrl =>
          createCourier({
            baseUrl,
            fetch: (input, init) => this.fetchApi.fetch(input, init),
            headers: {
              accept: 'application/json',
              'content-type': 'application/json',
            },
          }),
        );

      this.courierPromise = courierPromise.catch(error => {
        this.courierPromise = undefined;
        throw error;
      });
    }

    return this.courierPromise;
  }

  private async withProblemDetail<T>(request: Promise<T>): Promise<T> {
    try {
      return await request;
    } catch (error) {
      if (!(error instanceof CourierHttpError)) throw error;

      const statusText =
        typeof error.data === 'object' &&
        error.data !== null &&
        'title' in error.data &&
        typeof error.data.title === 'string'
          ? error.data.title
          : '';
      const response = new Response(JSON.stringify(error.data), {
        status: error.status,
        statusText,
        headers: error.headers,
      });
      throw await ResponseError.fromResponse(response);
    }
  }
}
